<script lang="ts">
  import { onMount } from 'svelte';
  import { AlertCircle, Check, ChevronDown, Loader2, RefreshCw, TrendingUp } from 'lucide-svelte';
  import { querystring, replace } from 'svelte-spa-router';
  import { type SessionSummary, fetchSessionsPaginated } from '$lib/api/sessions';
  import { type VixResponse, type VixTimeframe, fetchPnLvsVix } from '$lib/api/vix';
  import { Button } from '$lib/components/ui/button';
  import { Card } from '$lib/components/ui/card';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from '$lib/components/ui/dropdown-menu';
  import { Input } from '$lib/components/ui/input';
  import { SegmentedControl } from '$lib/components/ui/segmented';
  import { Skeleton } from '$lib/components/ui/skeleton';
  import AppShell from './AppShell.svelte';
  import CopyableSessionId from './CopyableSessionId.svelte';
  import VixDualChart from '../charts/VixDualChart.svelte';

  // ============================================================
  // <VixAnalysis> - PR-3 (port del VIX Analysis tab de v1, repensado).
  //
  // Cruza el P&L acumulado BT-style de una sesion live contra el CBOE VIX
  // (Yahoo ^VIX) para visualizar correlacion volatilidad/performance.
  //
  // Layout (top-down):
  //   - Header eyebrow + title + helper text
  //   - Toolbar: session dropdown (combobox-style filtrable) + manual input
  //     (14 digitos para pegar sessionId) + toggle 1m/5m + Load button
  //   - Loading / Empty / Error states
  //   - 5 KPI cards luxury (text-gold font-serif): Correlation, Total P&L,
  //     Win Rate, VIX Range, Data Quality
  //   - <VixDualChart> 520px (P&L arriba 60%, VIX abajo 40%, sync crosshairs)
  //
  // Convenciones DESIGN.md / PRODUCT.md respetadas:
  //   - tabular-nums + font-mono en todo dato numerico
  //   - color por signo (success/destructive), nunca por tolerancia
  //   - text-eyebrow + text-display para el header
  //   - text-gold font-serif para KPI labels (editorial synthesis)
  //   - Sin nesting de cards (KPIs como flex grid, no Card adentro de Card)
  //   - Lightweight Charts (no Plotly)
  // ============================================================

  // Hash routing: /vix?s=<sessionId>&tf=1m|5m
  let selectedSessionId = $state<string | null>(null);
  let manualInput = $state('');
  let timeframe = $state<VixTimeframe>('5m');

  let liveSessions = $state<SessionSummary[]>([]);
  let sessionsLoading = $state(true);

  let vixData = $state<VixResponse | null>(null);
  let vixLoading = $state(false);
  let vixError = $state<string | null>(null);

  // Lee query params al montar (deep-link)
  function readQueryParams() {
    const qs = new URLSearchParams($querystring ?? '');
    const s = qs.get('s');
    const tf = qs.get('tf');
    if (s && /^\d{14}$/.test(s)) {
      selectedSessionId = s;
      manualInput = s;
    }
    if (tf === '1m' || tf === '5m') timeframe = tf;
  }

  function syncQueryParams() {
    const qs = new URLSearchParams();
    if (selectedSessionId) qs.set('s', selectedSessionId);
    qs.set('tf', timeframe);
    replace(`/vix?${qs}`);
  }

  async function loadSessions() {
    sessionsLoading = true;
    try {
      // Top 80 sessions live (mismo patron que v1).
      const res = await fetchSessionsPaginated({
        page: 1,
        pageSize: 80,
        sort: 'startTime',
        dir: 'desc',
        status: 'live',
      });
      liveSessions = res.sessions;
    } catch (err) {
      console.warn('[vix] loadSessions fallo', err);
    } finally {
      sessionsLoading = false;
    }
  }

  async function loadVix() {
    if (!selectedSessionId) return;
    vixLoading = true;
    vixError = null;
    try {
      vixData = await fetchPnLvsVix(selectedSessionId, timeframe);
      syncQueryParams();
    } catch (err) {
      vixError = err instanceof Error ? err.message : 'Failed to load VIX data';
      vixData = null;
    } finally {
      vixLoading = false;
    }
  }

  function onManualInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (/^\d{14}$/.test(manualInput.trim())) {
        selectedSessionId = manualInput.trim();
        void loadVix();
      }
    }
  }

  function selectSession(sessionId: string) {
    selectedSessionId = sessionId;
    manualInput = sessionId;
    void loadVix();
  }

  function setTimeframe(tf: VixTimeframe) {
    if (tf === timeframe) return;
    timeframe = tf;
    if (selectedSessionId) void loadVix();
  }

  onMount(() => {
    readQueryParams();
    void loadSessions();
    if (selectedSessionId) void loadVix();
  });

  // ----- Formatters -----
  function fmtMoney(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtCorrelation(c: number | null): string {
    if (c == null) return '—';
    const sign = c > 0 ? '+' : c < 0 ? '-' : '';
    return `${sign}${Math.abs(c).toFixed(3)}`;
  }

  // Mapeo de correlacion → label semantico (mismo que V1, con tokens del theme).
  function correlationLabel(c: number | null): { label: string; cls: string } {
    if (c == null) return { label: 'no data', cls: 'text-muted-foreground' };
    if (c <= -0.5) return { label: 'strong inverse', cls: 'text-success' };
    if (c <= -0.2) return { label: 'mild inverse', cls: 'text-success/70' };
    if (c < 0.2) return { label: 'weak / none', cls: 'text-warning' };
    if (c < 0.5) return { label: 'mild positive', cls: 'text-primary' };
    return { label: 'strong positive', cls: 'text-destructive' };
  }

  // Format de SessionSummary -> texto del item del listado.
  // "<sessionId> · <symbol> · <bracket>"
  function fmtSessionOption(s: SessionSummary): string {
    const bracket = s.dailyStartTime && s.dailyEndTime ? `${s.dailyStartTime}–${s.dailyEndTime}` : '';
    return [s.sessionId, s.symbol, bracket].filter(Boolean).join(' · ');
  }

  // ----- Derived -----
  let stats = $derived(vixData?.stats ?? null);
  let corrInfo = $derived(correlationLabel(stats?.correlation ?? null));
  let winRate = $derived.by((): string => {
    if (!stats || stats.tradesTotal === 0) return '—';
    return `${((stats.tradesWin / stats.tradesTotal) * 100).toFixed(1)}%`;
  });
  // Para el trigger del DropdownMenu: la session actualmente seleccionada
  // (si esta en la lista). Si el operador pego un sessionId manual que no
  // matchea ninguna live, el trigger muestra el sessionId sin contexto.
  let selectedSession = $derived(
    selectedSessionId ? liveSessions.find((s) => s.sessionId === selectedSessionId) ?? null : null,
  );
</script>

<AppShell>
  <section class="mx-auto w-full max-w-[1600px] px-4 py-8 md:px-8 md:py-10">
    <!-- Header -->
    <header class="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div class="flex flex-col gap-1">
        <span class="text-eyebrow">Volatility</span>
        <h1 class="text-display text-3xl md:text-4xl">VIX Analysis</h1>
        <p class="text-muted-foreground mt-1 text-[13px]">
          Cumulative P&L vs CBOE VIX. Spot volatility regimes that align (or
          oppose) the strategy's edge.
        </p>
      </div>
      {#if selectedSessionId}
        <Button
          variant="outline"
          size="sm"
          onclick={() => void loadVix()}
          disabled={vixLoading}
        >
          <RefreshCw
            class={['size-3.5', vixLoading && 'animate-spin'].filter(Boolean).join(' ')}
          />
          Refresh
        </Button>
      {/if}
    </header>

    <!-- Toolbar: session picker (DropdownMenu) + manual input + timeframe (SegmentedControl) -->
    <div class="mb-6 flex flex-col gap-3 md:flex-row md:items-end">
      <!-- Recent live sessions: DropdownMenu con la lista, mismo patron que
           el resto del dashboard (consistente con SessionDetailPanel "Group by day"). -->
      <div class="flex flex-col gap-1.5 md:w-[420px]">
        <span class="text-muted-foreground text-[10px] uppercase tracking-wider">
          Recent live sessions
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger>
            {#snippet child({ props })}
              <Button
                {...props}
                variant="outline"
                class="h-9 w-full justify-between font-mono text-xs tabular-nums"
                disabled={sessionsLoading || liveSessions.length === 0}
              >
                <span class="truncate">
                  {#if sessionsLoading}
                    Loading…
                  {:else if selectedSession}
                    {fmtSessionOption(selectedSession)}
                  {:else if selectedSessionId}
                    {selectedSessionId} · (manual)
                  {:else if liveSessions.length === 0}
                    No live sessions
                  {:else}
                    Pick a session…
                  {/if}
                </span>
                <ChevronDown class="size-3.5 shrink-0 opacity-60" />
              </Button>
            {/snippet}
          </DropdownMenuTrigger>
          <DropdownMenuContent class="max-h-[60vh] w-[420px] overflow-y-auto" align="start">
            <DropdownMenuLabel>Top {liveSessions.length} recent live</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {#each liveSessions as s (s.sessionId)}
              {@const active = selectedSessionId === s.sessionId}
              <DropdownMenuItem onclick={() => selectSession(s.sessionId)}>
                <span class="flex-1 truncate font-mono text-xs tabular-nums">
                  {fmtSessionOption(s)}
                </span>
                {#if active}
                  <Check class="text-primary size-3.5" />
                {/if}
              </DropdownMenuItem>
            {/each}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <!-- Manual input -->
      <div class="flex flex-col gap-1.5 md:w-[200px]">
        <span class="text-muted-foreground text-[10px] uppercase tracking-wider">
          Or paste sessionId (14 digits)
        </span>
        <Input
          type="text"
          maxlength={14}
          placeholder="20260601115708"
          bind:value={manualInput}
          onkeydown={onManualInputKeydown}
          class="font-mono tabular-nums"
        />
      </div>

      <!-- Timeframe toggle: SegmentedControl shadcn-style. -->
      <div class="flex flex-col gap-1.5">
        <span class="text-muted-foreground text-[10px] uppercase tracking-wider">
          Bucket
        </span>
        <SegmentedControl
          options={[
            { value: '1m' as VixTimeframe, label: '1m' },
            { value: '5m' as VixTimeframe, label: '5m' },
          ]}
          value={timeframe}
          onChange={setTimeframe}
          ariaLabel="VIX timeframe"
        />
      </div>

      <!-- Load button (fallback al click del dropdown / Enter del input) -->
      <Button
        size="default"
        onclick={() => {
          if (/^\d{14}$/.test(manualInput.trim())) {
            selectSession(manualInput.trim());
          }
        }}
        disabled={!manualInput || !/^\d{14}$/.test(manualInput.trim()) || vixLoading}
      >
        Load
      </Button>
    </div>

    <!-- States -->
    {#if !selectedSessionId}
      <Card class="border-dashed py-12">
        <div class="text-muted-foreground flex flex-col items-center gap-2 text-center text-sm">
          <TrendingUp class="size-8 opacity-40" />
          <p>Pick a session above to see how its P&L tracked against VIX.</p>
        </div>
      </Card>
    {:else if vixLoading}
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
          {#each Array(5) as _, i (i)}
            <Skeleton class="h-24" />
          {/each}
        </div>
        <Skeleton class="h-[520px]" />
      </div>
    {:else if vixError}
      <Card class="border-destructive/40 bg-destructive/5">
        <div class="text-destructive flex items-center gap-2 px-4 py-3 text-sm">
          <AlertCircle class="size-4" />
          <span>{vixError}</span>
        </div>
      </Card>
    {:else if vixData}
      <!-- KPI cards: 5 columnas con text-gold font-serif (editorial synthesis) -->
      <div
        class="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5"
      >
        <!-- Correlation -->
        <Card class="px-4 py-3">
          <div class="text-eyebrow text-gold font-serif">Correlation</div>
          <div class="mt-1 flex items-baseline gap-2">
            <span class="font-mono text-2xl font-semibold tabular-nums">
              {fmtCorrelation(stats?.correlation ?? null)}
            </span>
          </div>
          <div class={['mt-0.5 text-[10px] uppercase tracking-wider', corrInfo.cls].join(' ')}>
            {corrInfo.label}
          </div>
        </Card>

        <!-- Total P&L (BT-style) -->
        <Card class="px-4 py-3">
          <div class="text-eyebrow text-gold font-serif">Total P&L</div>
          <div
            class={[
              'mt-1 font-mono text-2xl font-semibold tabular-nums',
              (stats?.pnlTotal ?? 0) > 0 && 'text-success',
              (stats?.pnlTotal ?? 0) < 0 && 'text-destructive',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {fmtMoney(stats?.pnlTotal ?? 0)}
          </div>
          <div class="text-muted-foreground mt-0.5 text-[10px] uppercase tracking-wider">
            {stats?.tradesTotal ?? 0} trades · BT
          </div>
        </Card>

        <!-- Win Rate -->
        <Card class="px-4 py-3">
          <div class="text-eyebrow text-gold font-serif">Win Rate</div>
          <div class="mt-1 font-mono text-2xl font-semibold tabular-nums">{winRate}</div>
          <div class="text-muted-foreground mt-0.5 text-[10px] uppercase tracking-wider">
            {stats?.tradesWin ?? 0}W / {stats?.tradesLoss ?? 0}L
          </div>
        </Card>

        <!-- VIX Range -->
        <Card class="px-4 py-3">
          <div class="text-eyebrow text-gold font-serif">VIX Range</div>
          <div class="mt-1 font-mono text-base font-semibold tabular-nums">
            {stats?.vixMin != null ? stats.vixMin.toFixed(2) : '—'}
            <span class="text-muted-foreground">→</span>
            {stats?.vixMax != null ? stats.vixMax.toFixed(2) : '—'}
          </div>
          <div class="text-muted-foreground mt-0.5 text-[10px] uppercase tracking-wider">
            {#if stats?.vixDelta != null}
              Δ {stats.vixDelta > 0 ? '+' : ''}{stats.vixDelta.toFixed(2)}
            {:else}
              No data
            {/if}
          </div>
        </Card>

        <!-- Data Quality -->
        <Card class="px-4 py-3">
          <div class="text-eyebrow text-gold font-serif">Data Quality</div>
          <div
            class={[
              'mt-1 font-mono text-2xl font-semibold tabular-nums',
              stats?.vixFetchOk ? 'text-success' : 'text-destructive',
            ].join(' ')}
          >
            {stats?.vixFetchOk ? 'OK' : 'FAIL'}
          </div>
          <div class="text-muted-foreground mt-0.5 text-[10px] uppercase tracking-wider">
            {stats?.vixBarsReceived ?? 0} VIX bars
            {#if stats?.vixFetchError}
              · {stats.vixFetchError}
            {/if}
          </div>
        </Card>
      </div>

      <!-- Symbol + sessionId chip arriba del chart -->
      <div class="mb-2 flex items-center gap-2 text-xs">
        <span class="text-muted-foreground uppercase tracking-wider">Session</span>
        <CopyableSessionId sessionId={vixData.session.sessionId} />
        <span class="text-primary font-mono font-semibold">{vixData.session.symbol}</span>
        <span class="text-muted-foreground font-mono">· {vixData.session.timeframe}</span>
      </div>

      <!-- Dual chart -->
      <Card class="overflow-hidden p-4">
        <VixDualChart
          timeline={vixData.timeline}
          trades={vixData.trades}
          timeframe={vixData.session.timeframe}
          height={520}
        />
        {#if !vixData.timeline.some((r) => r.vix != null)}
          <div class="text-muted-foreground mt-3 flex items-start gap-2 text-xs">
            <span class="text-warning">⚠</span>
            <span>
              VIX (^VIX) only updates during SPX regular trading hours
              (09:30–16:15 ET). This session ran outside that window — no VIX
              overlay is available. The P&L chart above shows trade clusters
              with their natural gaps.
            </span>
          </div>
        {/if}
      </Card>
    {/if}
  </section>
</AppShell>
